import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, X, Save, Loader2 } from "lucide-react";
import { admin } from "@/lib/api";
import { Field, TabHeader, Badge, inputCls, textareaCls, slugify, fmtDate } from "@/admin/ui";

const bodyToText = (body) =>
  (body || []).map((b) => (b.type === "h2" ? `## ${b.text}` : b.text)).join("\n\n");

const textToBody = (text) =>
  text.split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean).map((p) =>
    p.startsWith("## ") ? { type: "h2", text: p.slice(3).trim() } : { type: "p", text: p }
  );

const EMPTY = {
  title: "", slug: "", category: "", status: "draft",
  date: new Date().toISOString().slice(0, 10), publishDate: "",
  readTime: "5 min read", excerpt: "", bodyText: "",
};

const STATUS_TONE = { published: "forest", draft: "muted", scheduled: "terracotta" };

export default function BlogTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => admin.list("posts").then(setItems).catch(() => toast.error("Could not load posts"));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.title), body: textToBody(form.bodyText) };
      delete payload.bodyText;
      if (form.id) await admin.update("posts", form.id, payload);
      else await admin.create("posts", payload);
      toast.success(form.id ? "Post updated" : "Post created");
      setForm(null);
      load();
    } catch {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    await admin.remove("posts", p.id);
    toast.success("Post deleted");
    load();
  };

  if (form) {
    return (
      <div data-testid="admin-post-form">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-semibold text-foreground">
            {form.id ? "Edit Post" : "New Post"}
          </h1>
          <button onClick={() => setForm(null)} data-testid="admin-post-cancel" className="btn-secondary !px-4 !py-2 !text-xs">
            <X className="h-3.5 w-3.5" /> Cancel
          </button>
        </div>
        <div className="mt-8 grid gap-5 rounded-3xl border border-border bg-card p-7 shadow-soft sm:grid-cols-2">
          <Field label="Title"><input className={inputCls} value={form.title} onChange={set("title")} data-testid="post-title-input" /></Field>
          <Field label="Slug" hint="Leave blank to auto-generate from the title.">
            <input className={inputCls} value={form.slug} onChange={set("slug")} data-testid="post-slug-input" />
          </Field>
          <Field label="Category" hint="Type a new one or reuse an existing category.">
            <input className={inputCls} value={form.category} onChange={set("category")} list="post-categories" data-testid="post-category-input" />
            <datalist id="post-categories">
              {[...new Set((items || []).map((p) => p.category))].map((c) => <option key={c} value={c} />)}
            </datalist>
          </Field>
          <Field label="Status">
            <select className={inputCls} value={form.status} onChange={set("status")} data-testid="post-status-select">
              <option value="draft">Draft (hidden)</option>
              <option value="published">Published</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </Field>
          {form.status === "scheduled" && (
            <Field label="Publish on" hint="The post goes live automatically on this date.">
              <input type="datetime-local" className={inputCls} value={form.publishDate} onChange={set("publishDate")} data-testid="post-publish-date-input" />
            </Field>
          )}
          <Field label="Display date"><input type="date" className={inputCls} value={form.date} onChange={set("date")} data-testid="post-date-input" /></Field>
          <Field label="Read time"><input className={inputCls} value={form.readTime} onChange={set("readTime")} data-testid="post-readtime-input" /></Field>
          <div className="sm:col-span-2">
            <Field label="Excerpt" hint="One or two sentences shown on the article card.">
              <textarea className={textareaCls} rows={2} value={form.excerpt} onChange={set("excerpt")} data-testid="post-excerpt-input" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Body" hint="Separate paragraphs with a blank line. Start a line with '## ' to make it a heading.">
              <textarea className={textareaCls} rows={16} value={form.bodyText} onChange={set("bodyText")} data-testid="post-body-input" />
            </Field>
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-post-save" className="btn-primary mt-6 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save Post"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-posts-tab">
      <TabHeader
        title="Blog Posts"
        description="Write, edit, schedule, and publish articles."
        actionLabel="New Post"
        onAdd={() => setForm({ ...EMPTY })}
        testId="admin-add-post"
      />
      <div className="mt-8 space-y-3">
        {(items || []).map((p) => (
          <div key={p.id} data-testid={`admin-post-row-${p.slug}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-soft">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{p.title}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{p.category} · {fmtDate(p.date)}</p>
            </div>
            <Badge tone={STATUS_TONE[p.status] || "muted"}>{p.status}</Badge>
            <button onClick={() => setForm({ ...EMPTY, ...p, bodyText: bodyToText(p.body) })} data-testid={`admin-post-edit-${p.slug}`} aria-label={`Edit ${p.title}`} className="rounded-full border border-border p-2 text-muted-foreground transition-colors duration-300 hover:border-terracotta-500 hover:text-terracotta-500">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => remove(p)} data-testid={`admin-post-delete-${p.slug}`} aria-label={`Delete ${p.title}`} className="rounded-full border border-border p-2 text-muted-foreground transition-colors duration-300 hover:border-destructive hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {items && items.length === 0 && (
          <p className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No posts yet — create your first one.</p>
        )}
      </div>
    </div>
  );
}
